/**
 * Hook for updating shipment/container data
 *
 * Provides mutation for updating shipment fields with optimistic updates
 * and automatic cache invalidation.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPut } from "@/lib/api-client";
import { CONTAINERS_RECAP_QUERY_KEYS } from "./use-containers-recap";

// ============================================================================
// TYPES
// ============================================================================

export interface ShipmentUpdatePayload {
  // Dates
  actual_departure_date?: string | null;
  estimated_departure_date?: string | null;
  actual_arrival_date?: string | null;
  estimated_arrival_date?: string | null;

  // Risk (0-100 scale, will be converted to 0.0-1.0 by backend)
  risk_score?: number | null;

  // Status
  status?: string;
  physical_status?: string;

  // Port
  destination_port_id?: string | null;

  // Value
  total_value_usd?: number | null;
  currency?: string;

  // Other fields
  container_number?: string;
  booking_number?: string;
  bl_number?: string;
  vessel_name?: string;
  voyage_number?: string;
  carrier_name?: string;
  cargo_description?: string;
  hs_code_primary?: string;
  total_weight_kg?: number | null;
  total_volume_cbm?: number | null;
  total_packages?: number | null;
  temperature_setpoint_celsius?: number | null;
  internal_notes?: string;
  customer_notes?: string;
  special_instructions?: string;
}

export interface ShipmentUpdateResponse {
  container_id: string;
  container_number: string | null;
  status: string;
  physical_status: string;
  risk_score: number | null;
  total_value_usd: number | null;
  destination_port_id: string | null;
  destination_port_name: string | null;
  actual_departure_date: string | null;
  estimated_departure_date: string | null;
  actual_arrival_date: string | null;
  estimated_arrival_date: string | null;
  updated_at: string;
}

// ============================================================================
// UPDATE HOOK
// ============================================================================

interface UseShipmentUpdateOptions {
  onSuccess?: (data: ShipmentUpdateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update a shipment/container
 *
 * @param options - Optional callbacks for success/error
 *
 * Usage:
 * ```ts
 * const { mutate: updateShipment, isPending } = useShipmentUpdate({
 *   onSuccess: () => toast.success('Updated!'),
 *   onError: (err) => toast.error(err.message),
 * });
 *
 * updateShipment({
 *   shipmentId: 'uuid',
 *   payload: { risk_score: 0.75 }
 * });
 * ```
 */
export function useShipmentUpdate(options?: UseShipmentUpdateOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      payload,
    }: {
      shipmentId: string;
      payload: ShipmentUpdatePayload;
    }) => {
      // Convert risk_score from 0-100 to 0.0-1.0 if provided
      const processedPayload = { ...payload };
      if (
        processedPayload.risk_score !== undefined &&
        processedPayload.risk_score !== null
      ) {
        processedPayload.risk_score = processedPayload.risk_score / 100;
      }

      return await apiPut<ShipmentUpdateResponse>(
        `/shipments/${shipmentId}`,
        processedPayload
      );
    },
    onSuccess: (data) => {
      // Invalidate containers recap queries to refresh data
      queryClient.invalidateQueries({
        queryKey: CONTAINERS_RECAP_QUERY_KEYS.all,
      });

      // Invalidate single shipment query
      queryClient.invalidateQueries({
        queryKey: ["shipment", data.container_id],
      });

      // Invalidate activities to show the new modification event
      queryClient.invalidateQueries({
        queryKey: ["activities"],
      });

      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}

// ============================================================================
// FIELD MAPPING HELPERS
// ============================================================================

/**
 * Maps frontend field names to API field names
 */
export const FIELD_TO_API_MAP: Record<string, keyof ShipmentUpdatePayload> = {
  actualDepartureDate: "actual_departure_date",
  estimatedDepartureDate: "estimated_departure_date",
  actualArrivalDate: "actual_arrival_date",
  estimatedArrivalDate: "estimated_arrival_date",
  riskLevel: "risk_score",
  physicalStatus: "physical_status",
  status: "status",
  destinationPortId: "destination_port_id",
  totalValueUsd: "total_value_usd",
  value: "total_value_usd",
};

/**
 * Risk level to score mapping (for dropdown)
 */
export const RISK_LEVEL_TO_SCORE: Record<string, number> = {
  low: 15,
  medium: 35,
  high: 65,
  critical: 85,
};

/**
 * Score to risk level mapping
 */
export function scoreToRiskLevel(
  score: number | null | undefined
): "low" | "medium" | "high" | "critical" {
  if (score === null || score === undefined) return "low";
  const percent = score * 100;
  if (percent < 25) return "low";
  if (percent < 50) return "medium";
  if (percent < 75) return "high";
  return "critical";
}
